import {Injectable, signal, DestroyRef, inject} from '@angular/core';
import {Employee, EmployeePositionId} from '../../classes/models/employee/Employee';
import {Rota} from '../../classes/models/rota/Rota';
import {EmployeeCapacity} from '../../classes/models/employee/EmployeeCapacity';
import moment from 'moment';
import {RotaAssignment} from '../../classes/models/rota/RotaAssignment';
import {PredictionRotaWeek} from '../../classes/models/PredictionRotaWeek';
import {DailyFootfallPrediction} from '../../classes/models/prediction/DailyFootfallPrediction';
import {EmployeeCapacityService} from '../employee/employee-capacity.service';
import {DailyRequiredEmployeePrediction} from '../../classes/models/prediction/DailyRequiredEmployeePrediction';
import {DayOfWeek, dayOfWeekValues} from '../../classes/enums/DayOfWeek';
import {EmployeeManagerSettingsService} from '../http/employee/employee-manager-settings.service';
import {ManagerSettings} from '../../classes/models/employee/ManagerSettings';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

/**
 * This service contains rota data, shared between rota weekly and rota daily pages.
 */
@Injectable({
  providedIn: 'root'
})
export class RotaDataService {

  /** A rota currently opened by the user. */
  readonly rota = signal<Rota | null>(null);

  /** All employees of the rota's store. */
  readonly employees = signal<Employee[]>([]);

  /** A map from DayOfWeek to total working hours of that date. */
  readonly workingHoursByDay = signal<Map<DayOfWeek, number>>(new Map());

  /** A map of employee identifier to their working hours. */
  readonly workingHoursByEmployee = signal<Map<string, number>>(new Map());

  /** A map of employee identifier to their contract hours. */
  readonly contractHoursByEmployee = signal<Map<string, number>>(new Map());

  /** A map from DayOfWeek to predicted/actual employee capacity for a specific hour. */
  readonly employeeCapacityByDay = signal<Map<DayOfWeek, EmployeeCapacity[]>>(new Map());

  /** A map of employee identifier to whether they're on holiday all week. */
  readonly employeeOnHolidayAllWeek = signal<Map<string, boolean>>(new Map());

  /** A map from a manager (Employee) to a flag indicating if they can do sales. */
  readonly canDoSalesByEmployee = signal<Map<Employee, boolean>>(new Map());

  private readonly employeeCapacityService = inject(EmployeeCapacityService);
  private readonly employeeManagerSettingsService = inject(EmployeeManagerSettingsService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.clearAllData();
  }

  /**
   * Clears all rota data.
   */
  clearAllData(): void {
    this.rota.set(null);
    this.employees.set([]);

    const workingHoursByDay = new Map<DayOfWeek, number>();
    const employeeCapacityByDay = new Map<DayOfWeek, EmployeeCapacity[]>();
    dayOfWeekValues().forEach(dayOfWeek => {
      workingHoursByDay.set(dayOfWeek, 0);
      employeeCapacityByDay.set(dayOfWeek, []);
    });

    this.workingHoursByDay.set(workingHoursByDay);
    this.workingHoursByEmployee.set(new Map());
    this.contractHoursByEmployee.set(new Map());
    this.employeeCapacityByDay.set(employeeCapacityByDay);
    this.employeeOnHolidayAllWeek.set(new Map());
    this.canDoSalesByEmployee.set(new Map());
  }

  /**
   * Calculates all data, needed to display a rota.
   * @param predictionRotaWeek week information of footfall predictions and rota.
   * @param rota the rota, which data should be calculated.
   * @param employees all employees of the store.
   * @param requiredEmployeesPredictions a list of required minimum employee predictions for a week.
   * Calculates all data needed to display a rota.
   */
  calculateAndSaveData(
          predictionRotaWeek: PredictionRotaWeek,
          rota: Rota,
          employees: Employee[],
          requiredEmployeesPredictions: DailyRequiredEmployeePrediction[]
  ): void {
    this.clearAllData();
    this.rota.set(rota);
    this.employees.set(employees);
    this.calculateWorkingHours();
    this.calculateAbsenceRelatedData();
    this.assignEmployeesCapacityByDate(predictionRotaWeek.dailyPredictions, requiredEmployeesPredictions);
    this.assignManagerCanDoSalesThisWeek(employees, predictionRotaWeek.from);
  }

  private calculateWorkingHours(): void {
    const workingHoursByEmployee = new Map(this.workingHoursByEmployee());
    const workingHoursByDay = new Map(this.workingHoursByDay());

    this.rota()?.rotaAssignments.forEach(assignment => {
      this.addWorkingHoursByKey(workingHoursByEmployee, assignment, assignment.employeePositionId.value);
      this.addWorkingHoursByKey(workingHoursByDay, assignment, assignment.dayOfWeek);
    });

    this.workingHoursByEmployee.set(workingHoursByEmployee);
    this.workingHoursByDay.set(workingHoursByDay);
  }

  private calculateAbsenceRelatedData(): void {
    const holidays = new Map<string, number>();
    this.rota()?.fullDayAbsenceAssignments.forEach(assignment => {
      const prev = holidays.get(assignment.employeePositionId.value);
      holidays.set(assignment.employeePositionId.value, prev ? prev + 1 : 1);
    });

    const contractHours = new Map(this.contractHoursByEmployee());
    const onHolidayAllWeek = new Map(this.employeeOnHolidayAllWeek());

    this.employees().forEach(employee => {
      contractHours.set(
              employee.employeePositionId.value,
              this.getEmployeeContractHours(employee, holidays)
      );
      onHolidayAllWeek.set(
              employee.employeePositionId.value,
              this.onHolidayAllWeek(employee.employeePositionId)
      );
    });

    this.contractHoursByEmployee.set(contractHours);
    this.employeeOnHolidayAllWeek.set(onHolidayAllWeek);
  }

  private assignEmployeesCapacityByDate(
          footfallPredictions: DailyFootfallPrediction[],
          requiredEmployeesPredictions: DailyRequiredEmployeePrediction[]
  ): void {
    this.employeeCapacityByDay.set(
            this.employeeCapacityService.findEmployeesCapacityByDate(
                    this.rota(),
                    footfallPredictions,
                    requiredEmployeesPredictions
            )
    );
  }

  private addWorkingHoursByKey(
          map: Map<any, number>,
          assignment: RotaAssignment,
          key: string | DayOfWeek
  ): void {
    const startTime = moment(assignment.startTime, 'HH:mm');
    const endTime = moment(assignment.endTime, 'HH:mm');
    const hours = endTime.diff(startTime, 'minutes') / 60;
    const hoursWithoutBreak = this.removeBreakTime(hours);
    const current = map.get(key) ?? 0;
    map.set(key, current + hoursWithoutBreak);
  }

  private removeBreakTime(workingHours: number): number {
    const breakDuration = 0.5;
    const minHoursToAddBreak = 6;
    return workingHours >= minHoursToAddBreak
            ? workingHours - breakDuration
            : workingHours;
  }

  private getEmployeeContractHours(employee: Employee, holidays: Map<string, number>): number {
    const weeklyFreeDays = employee.contractHours >= 35 ? 2 : 4;
    const holidayEquivalent = Math.round(employee.contractHours / (7 - weeklyFreeDays));
    const employeeHolidays = holidays.get(employee.employeePositionId.value) ?? 0;
    const employeeHolidayHours = holidayEquivalent * employeeHolidays;
    return employeeHolidayHours > employee.contractHours
            ? 0
            : employee.contractHours - employeeHolidayHours;
  }

  private onHolidayAllWeek(employeePositionId: EmployeePositionId): boolean {
    const rota = this.rota();
    if (!rota) return false;
    const hasNoAssignments = rota.rotaAssignments
            .filter(item => item.employeePositionId.value === employeePositionId.value).length === 0;
    const isOnHolidays = rota.fullDayAbsenceAssignments
            .filter(item => item.employeePositionId.value === employeePositionId.value).length > 0;
    return hasNoAssignments && isOnHolidays;
  }

  private assignManagerCanDoSalesThisWeek(employees: Employee[], currentWeekStart: string): void {
    employees
            .filter(employee => employee.hasManagerRole())
            .forEach(employee => {
              this.employeeManagerSettingsService
                      .getManagerSettings(employee.employeePositionId)
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe(managerSettings => {
                        const canDoSales = this.canDoSalesThisWeek(managerSettings, currentWeekStart);
                        // Create a new Map to trigger signal reactivity
                        this.canDoSalesByEmployee.update(current => {
                          const updated = new Map(current);
                          updated.set(employee, canDoSales);
                          return updated;
                        });
                      });
            });
  }

  private canDoSalesThisWeek(managerSettings: ManagerSettings[], currentWeekStart: string): boolean {
    const filtered = managerSettings.filter(item => item.startDate <= currentWeekStart);
    if (filtered.length === 0) return false;
    const current = filtered.reduce((prev, curr) =>
            curr.startDate > prev.startDate ? curr : prev
    );
    return current.canDoSales;
  }
}